/**
 * 메일·알림에서 안건 하나를 지목해 여는 길.
 *
 * <p>이 앱에는 <b>화면마다 주소가 없다</b>. 탭 기반이라 `/scm` · `/home` 두 개뿐이고, 어떤
 * 안건을 열었는지는 주소에 남지 않는다. 그래서 "그 안건으로 가는 링크" 를 만들려면 주소에
 * 실어 온 뜻을 앱이 받아서 대신 열어 주는 수밖에 없다.
 *
 * <p>주소는 `https://앱주소/?agenda=123` 한 가지다. 새 라우트를 만들지 않은 이유 — 로그인
 * 여부에 따라 App 이 어떤 주소든 `/signin` 이나 `/home` 으로 갈아치우기 때문에, 라우트를
 * 늘려도 살아남지 못한다.
 *
 * <p><b>그래서 주소를 모듈이 읽히는 시점에 한 번 훔쳐 둔다.</b> 이 파일은 App 이 그리기 전에
 * import 되므로 라우터가 주소를 바꾸기 전이고, 훔친 값은 sessionStorage 에 넣어 두어 로그인
 * 과정에서 페이지가 통째로 새로고침돼도 살아남는다.
 */

const QUERY_PARAM = "agenda";
const STORAGE_KEY = "plannel.pendingAgendaId";

/** 회의 안건 화면의 메뉴 키(TabMenuList lv3 의 reduxKey) */
export const MEETING_AGENDA_MENU_KEY = "MEETING_AGENDA";

/**
 * 이미 앱이 떠 있는 상태에서 안건을 열라고 알리는 신호.
 *
 * <p>화면이 탭으로 이미 열려 있으면 다시 mount 되지 않아 저장된 값을 읽을 기회가 없다.
 * 알림함 종처럼 앱 안에서 부르는 쪽은 이 이벤트를 같이 쏜다.
 */
export const AGENDA_OPEN_EVENT = "plannel:openAgenda";

/** sessionStorage 는 시크릿 창·차단 설정에서 던질 수 있다. 링크 하나 때문에 앱이 죽으면 안 된다 */
const safely = (run, fallback = null) => {
  try {
    return run();
  } catch (e) {
    return fallback;
  }
};

const store = (agendaId) => safely(() => window.sessionStorage.setItem(STORAGE_KEY, String(agendaId)));

/** 지금 열어야 할 안건 id. 읽기만 하고 지우지 않는다 */
export const peekPendingAgendaId = () => safely(() => window.sessionStorage.getItem(STORAGE_KEY));

export const clearPendingAgendaId = () => safely(() => window.sessionStorage.removeItem(STORAGE_KEY));

/** 읽으면서 지운다. 화면이 실제로 열 때 쓴다 — 남겨 두면 다음에 들어올 때 또 열린다 */
export const consumePendingAgendaId = () => {
  const id = peekPendingAgendaId();
  if (id) {
    clearPendingAgendaId();
  }
  return id;
};

/** 앱 안에서 안건을 열어 달라고 요청한다(알림함 등). 화면이 닫혀 있으면 열릴 때 집어간다 */
export const requestAgenda = (agendaId) => {
  if (agendaId == null) {
    return;
  }
  store(agendaId);
  window.dispatchEvent(new CustomEvent(AGENDA_OPEN_EVENT, { detail: { agendaId: String(agendaId) } }));
};

/**
 * 주소에서 안건 id 를 훔쳐 둔다. 모듈이 읽힐 때 한 번만 돈다.
 *
 * <p>훔친 뒤 주소에서 파라미터를 지운다 — 남겨 두면 새로고침할 때마다 같은 안건이 다시
 * 열리고, 주소를 복사해 공유했을 때 받은 사람이 영문을 모른다.
 */
const captureFromUrl = () => {
  const id = safely(() => new URLSearchParams(window.location.search).get(QUERY_PARAM));
  if (!id || !/^[0-9]{1,19}$/.test(id)) {
    return;
  }
  store(id);
  safely(() => {
    const url = new URL(window.location.href);
    url.searchParams.delete(QUERY_PARAM);
    window.history.replaceState(null, "", url.pathname + url.search + url.hash);
  });
};

captureFromUrl();
